import React from 'react';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import { Avatar, Cell, HeaderCell, HeaderRow, Input, IUserProps, Label, Row, SidebarContent, SidebarHeader, SidebarList, Table, TableBody, TableHead, Tag, Text, User } from 'components';
import { theme, toRem } from 'design';
import { DisbursementMethod, IPayee } from 'types';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay, formatJobTitle } from 'utils';

// PRS === PaymentReviewSidebar
export const PRSHeader = styled(SidebarHeader)`
  @media (min-width: ${theme.breakpoint.large}px) {
    border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
    color: ${theme.color.neutral[900]};

    label,
    span {
      color: ${theme.color.neutral[900]};
    }
  }
`;

export const PRSHeaderSection = styled.section`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  overflow: hidden;

  @media (min-width: ${theme.breakpoint.large}px) {
    flex-flow: column nowrap;
    justify-content: flex-start;
    align-items: flex-start;
    margin-bottom: ${toRem(33)};
    padding-bottom: ${toRem(33)};
    border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
  }
`;

export const PRSHeaderContent = styled(SidebarList)`
  display: none;

  @media (min-width: ${theme.breakpoint.large}px) {
    display: flex;
    flex-flow: column nowrap;
  }

  > * {
    margin-bottom: ${toRem(22)};

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const PRSContent = styled(SidebarContent)`
  @media (min-width: ${theme.breakpoint.large}px) {
    margin-top: ${toRem(33)};
  }
`;

const DescriptionLayout = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(11)};
  width: 100%;
  word-break: break-word;
`;

interface PaymentDescriptionProps {
  description?: string;
}

export const PaymentDescription = ({ description, ...rest }: PaymentDescriptionProps) => (
  <DescriptionLayout {...rest}>
    <Label>Description</Label>
    <Text>{description || 'No description added for this payment'}</Text>
  </DescriptionLayout>
);

export const MobileDescription = styled.section`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin-bottom: ${toRem(33)};
  padding-bottom: ${toRem(33)};
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};

  @media (min-width: ${theme.breakpoint.large}px) {
    display: none;
  }
`;

export const DesktopDescription = styled.section`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin-bottom: ${toRem(33)};
  padding-bottom: ${toRem(33)};
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
  @media (max-width: ${theme.breakpoint.large}px) {
    display: none;
  }
`;

const ReviewPaymentsBreakpoints = [0, theme.breakpoint.small, theme.breakpoint.med, theme.breakpoint.large, 1100];

interface ISiteUsesHoldback {
  siteUsesHoldback: boolean;
}

const ReviewPaymentsTable = styled(Table)<ISiteUsesHoldback>`
  overflow: hidden;

  ${({ siteUsesHoldback }) =>
    !siteUsesHoldback &&
    css`
      && {
        display: none;
        border: none;
      }
    `}

  ${({ siteUsesHoldback }) =>
    siteUsesHoldback &&
    css`
      display: none;
      border: none;

      @media (min-width: ${ReviewPaymentsBreakpoints[1]}px) {
        display: none;
        border: none;
      }

      @media (min-width: ${ReviewPaymentsBreakpoints[2]}px) {
        display: table;
        border: ${toRem(1)} solid ${theme.color.neutral[200]};
      }

      @media (min-width: ${ReviewPaymentsBreakpoints[3]}px) {
        display: none;
        border: none;
      }

      @media (min-width: ${ReviewPaymentsBreakpoints[4]}px) {
        display: table;
        border: ${toRem(1)} solid ${theme.color.neutral[200]};
      }
    `}
`;

interface IReviewPayeeProps extends IUserProps {
  isLoading?: boolean;
  note?: string;
  tipAmount?: string;
  holdback?: string;
  paid: string;
  disbursementMethod: DisbursementMethod;
}

const ReviewPaymentsRow = ({ id, username, image, role, hasPaymentMethod, isLoading, tipAmount, holdback, paid, siteUsesHoldback, disbursementMethod, ...rest }: IReviewPayeeProps & ISiteUsesHoldback) => (
  <Row {...rest}>
    <Cell>
      <User id={id} username={username} image={image} role={role} hasPaymentMethod={hasPaymentMethod} stackable={false} isLoading={isLoading} data-cy="user-card" />
    </Cell>
    {siteUsesHoldback && (
      <>
        <Cell data-label="Gross" align="right" data-cy="tip-amount">
          {tipAmount}
        </Cell>

        <Cell data-label="Holdback" align="right" data-cy="holdback">
          {holdback || '$0.00'}
        </Cell>
      </>
    )}
    <Cell data-label="Net" align="right">
      <strong>{paid}</strong>
    </Cell>
    <Cell data-label="Disbursement-Method" align="center">
      <Tag label={disbursementMethod} appearance={disbursementMethod} />
    </Cell>
  </Row>
);

const ReviewPaymentsGrid = styled.ul<ISiteUsesHoldback>`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(1)} ${toRem(1)};
  width: 100%;
  border-radius: ${toRem(5.5)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  background-color: ${theme.color.neutral[200]};
  list-style: none;
  overflow: hidden;

  ${({ siteUsesHoldback }) =>
    siteUsesHoldback &&
    css`
      @media (min-width: ${ReviewPaymentsBreakpoints[2]}px) {
        display: none;
      }

      @media (min-width: ${ReviewPaymentsBreakpoints[3]}px) {
        display: grid;
      }

      @media (min-width: ${ReviewPaymentsBreakpoints[4]}px) {
        display: none;
      }
    `}
`;

const GridItemLayout = styled.li`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  background-color: ${theme.color.white};
`;

const GridItemContent = styled.section<ISiteUsesHoldback>`
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: ${toRem(16.5)};
  justify-items: start;
  width: 100%;
  height: ${toRem(66)};
  padding: 0 ${toRem(22)};

  ${({ siteUsesHoldback }) =>
    siteUsesHoldback &&
    css`
      grid-template-columns: 1fr;
      justify-items: center;
      height: auto;
      padding: ${toRem(22)} ${toRem(11)} ${toRem(11)};

      @media (min-width: ${ReviewPaymentsBreakpoints[1]}px) {
        padding: ${toRem(22)} ${toRem(22)};
      }
    `}
`;

const GridItemUser = styled.div<ISiteUsesHoldback>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;

  ${({ siteUsesHoldback }) =>
    siteUsesHoldback &&
    css`
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: ${toRem(5.5)};
      justify-items: center;
    `}
`;

const UserInfo = styled.div<ISiteUsesHoldback>`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  margin-left: ${toRem(11)};

  > * {
    &:first-child {
      margin-bottom: ${toRem(5.5)};
    }
  }

  ${({ siteUsesHoldback }) =>
    siteUsesHoldback &&
    css`
      justify-content: center;
      align-items: center;
      margin-top: ${toRem(5.5)};
      margin-left: 0;
    `}
`;

const Name = styled(Text).attrs({
  size: 'small',
})`
  font-weight: ${theme.font.weight.regular};
  line-height: 100%;
  color: ${theme.color.neutral[900]};
`;

const Details = styled(Text).attrs({
  size: 'xsmall',
})`
  color: ${theme.color.neutral[700]};
`;

const GridItemContentLayout = styled.ul<ISiteUsesHoldback>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0;
  background-color: transparent;

  @media (min-width: ${theme.breakpoint.small}px) {
    max-width: none;
  }

  ${({ siteUsesHoldback }) =>
    siteUsesHoldback &&
    css`
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: ${toRem(11)};
      height: auto;
      padding: ${toRem(11)};
      border-radius: ${toRem(5.5)};
      background-color: ${theme.color.neutral[100]};
      list-style: none;

      @media (min-width: ${theme.breakpoint.small}px) {
        max-width: ${toRem(300)};
      }
    `}
`;

// Note
export const Note = styled(motion.section)`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  padding: 0 ${toRem(22)};
  overflow: hidden;
`;

export const NoteInput = styled(Input)`
  flex-shrink: 0;
  margin-bottom: ${toRem(22)};
`;

const DetailItemLayout = styled.li<ISiteUsesHoldback>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: baseline;
  width: 100%;

  ${({ siteUsesHoldback }) =>
    siteUsesHoldback &&
    css`
      justify-content: space-between;
    `}

  label {
    font-weight: ${theme.font.weight.regular};
  }

  > * {
    line-height: 100%;
    color: ${theme.color.neutral[900]};
  }

  &:last-child {
    span {
      font-weight: ${theme.font.weight.medium};
    }
  }

  ${({ siteUsesHoldback }) =>
    siteUsesHoldback === false &&
    css`
      label {
        margin-right: ${toRem(8)};
      }
    `}
`;

const ReviewPaymentsGridItem = ({ id, username, image, role, hasPaymentMethod, isLoading, tipAmount, holdback, paid, siteUsesHoldback, ...rest }: IReviewPayeeProps & ISiteUsesHoldback) => (
  <GridItemLayout {...rest}>
    <GridItemContent siteUsesHoldback={siteUsesHoldback}>
      <GridItemUser siteUsesHoldback={siteUsesHoldback}>
        <Avatar size="medium" id={id} image={image} username={username} hasPaymentMethod={hasPaymentMethod} isLoading={isLoading} />
        <UserInfo siteUsesHoldback={siteUsesHoldback}>
          <Name>{username}</Name>
          <Details>{role}</Details>
        </UserInfo>
      </GridItemUser>

      <GridItemContentLayout siteUsesHoldback={siteUsesHoldback}>
        {siteUsesHoldback && (
          <>
            <DetailItemLayout siteUsesHoldback={siteUsesHoldback}>
              <Text size="small" as="label">
                Gross
              </Text>
              <Text size="small" as="span">
                {tipAmount}
              </Text>
            </DetailItemLayout>

            <DetailItemLayout siteUsesHoldback={siteUsesHoldback}>
              <Text size="small" as="label">
                Holdback
              </Text>
              <Text size="small" as="span">
                {holdback}
              </Text>
            </DetailItemLayout>
          </>
        )}

        <DetailItemLayout siteUsesHoldback={siteUsesHoldback}>
          <Text size="small" as="label">
            Net
          </Text>

          <Text data-cy="tip-amount" size="small" as="span" aria-label={`Paid: ${paid}`}>
            {paid}
          </Text>
        </DetailItemLayout>
      </GridItemContentLayout>
    </GridItemContent>
  </GridItemLayout>
);

interface IReviewPaymentsListProps {
  isLoading?: boolean;
  payees: IPayee[];
}

export const ReviewPaymentsList = ({ isLoading, payees, siteUsesHoldback }: IReviewPaymentsListProps & ISiteUsesHoldback) => (
  <>
    <ReviewPaymentsTable siteUsesHoldback={siteUsesHoldback}>
      <TableHead>
        <HeaderRow>
          <HeaderCell align="left">Name</HeaderCell>
          <HeaderCell align="right">Tip Amount</HeaderCell>
          <HeaderCell align="right">Holdback</HeaderCell>
          <HeaderCell align="right">Paid</HeaderCell>
          <HeaderCell />
        </HeaderRow>
      </TableHead>
      <TableBody>
        {payees.map((payee: IPayee, index: number) => (
          <ReviewPaymentsRow
            data-cy="review-payments-row"
            siteUsesHoldback={siteUsesHoldback}
            key={`${payee.name}-${index}`}
            id={payee.id}
            username={payee.name}
            image={payee.user?.avatarUrl}
            role={payee.job ? formatJobTitle(payee.job.title) : ''}
            hasPaymentMethod={!!payee.user?.card}
            tipAmount={(payee.tipAmount && centsToDollarsDisplay(payee.tipAmount)) || ''}
            holdback={(payee.holdbackAmount && centsToNegativeDollarsDisplay(payee.holdbackAmount)) || ''}
            paid={(payee.tipAmount && centsToDollarsDisplay(payee.tipAmount - (payee.holdbackAmount || 0))) || ''}
            isLoading={isLoading}
            disbursementMethod={payee.disbursementMethod}
          />
        ))}
      </TableBody>
    </ReviewPaymentsTable>

    <ReviewPaymentsGrid siteUsesHoldback={siteUsesHoldback}>
      {payees.map((payee: IPayee, index: number) => (
        <ReviewPaymentsGridItem
          data-cy="review-payments-grid-item"
          siteUsesHoldback={siteUsesHoldback}
          key={`${payee.name}-${index}`}
          id={payee.id}
          username={payee.name}
          image={payee.user?.avatarUrl}
          role={payee.job ? formatJobTitle(payee.job.title) : ''}
          hasPaymentMethod={!!payee?.user?.card}
          tipAmount={(payee.tipAmount && centsToDollarsDisplay(payee.tipAmount)) || ''}
          holdback={(payee.holdbackAmount && centsToNegativeDollarsDisplay(payee.holdbackAmount)) || ''}
          paid={(payee.tipAmount && centsToDollarsDisplay(payee.tipAmount - (payee.holdbackAmount || 0))) || ''}
          isLoading={isLoading}
          disbursementMethod={payee.disbursementMethod}
        />
      ))}
    </ReviewPaymentsGrid>
  </>
);
